/**
 * Schema → form field descriptors, carrying the LAYER-2 semantics — ADR-127 D2.
 *
 * WHY THIS FILE EXISTS AT ALL
 * --------------------------
 * D2: "Schema → form is solved off the shelf for the STRUCTURAL layer … The layer-2 semantics
 * are where this surface earns its keep, because no generator supplies them and they are
 * exactly what the corpus is full of." And: "A generated form that ignored layer 2 would be a
 * worse editor than the raw JSON it replaces."
 *
 * So this module's whole job is the five obligations D2 names:
 *
 *   R6  an unknown value renders as a distinct "not yet known" state, never a blank that
 *       looks like a real `false`
 *   R7  provenance renders as a "confirmed by / on" affordance
 *   R12 units render IN THE LABEL, so 0.6 and 20 are never confused
 *   R14 a deprecated field renders struck-through with its date
 *   R18 an externally-resolved field renders read-only with its resolver NAMED, so nobody
 *       edits a value the system will overwrite
 *
 * THE TRAP THIS FILE IS BUILT AROUND
 * ----------------------------------
 * **The form model reads the RAW schema. Validation reads the COMPILED one.** They are
 * different documents on purpose: `compile_schema()` materialises the annotations into layer-1
 * constructs and STRIPS every `x-` key, because the validating engines would ignore them.
 * A form built from the compiled document would therefore see no units, no deprecations, no
 * resolvers and no sentinel — and would render a perfectly plausible editor that silently
 * dropped every obligation above.
 *
 * That is not hypothetical: a temporal check in this programme once read the compiled schema
 * for exactly this reason and passed everything it was given. `dataFormModel.test.ts` asserts
 * the distinction directly by running the same schema through both and showing the compiled
 * one yields no layer-2 metadata.
 */

/** The kinds of control the form renders. Deliberately small — anything not modelled here
 *  belongs behind D2's "Raw / advanced" toggle rather than half-rendered. */
export type FieldKind =
  | 'string' | 'number' | 'integer' | 'boolean'
  | 'enum' | 'array' | 'object' | 'unsupported'

export interface Deprecation {
  since?: string
  note?: string
}

export interface FieldDescriptor {
  /** Dotted path into the instance: `properties.items[].wp_api_base`. */
  path: string
  /** The property name alone. */
  name: string
  /** What the operator reads. Carries the UNIT (R12) when the schema declares one. */
  label: string
  kind: FieldKind
  /** Inline help (R13). */
  description?: string
  /** Options for an enum, rendered as a picker rather than a free-text box. */
  options?: unknown[]
  /** R12, kept separately from the label so a table can column-align it. */
  unit?: string
  /** R14. Present means struck-through, with `since` shown. */
  deprecated?: Deprecation
  /**
   * R6. The value that stands for "not yet known".
   *
   * The renderer MUST offer this as its own state. A blank is not acceptable: for a boolean
   * field a blank looks exactly like a real `false`, which is the confusion R6 exists to
   * prevent and the reason the corpus has 13 `__GEORGE_QUESTION__` values rather than
   * guesses.
   */
  unknownSentinel?: unknown
  /** R18. Read-only, with the resolver named — nobody should edit a value the system will
   *  overwrite on the next resolution. */
  resolvedBy?: string
  /** True when the schema requires it. */
  required: boolean
  /** Nested descriptors for an object, or for an array's record. */
  children?: FieldDescriptor[]
  /** For an array: the declared bound (ADR-126 D4 / ADR-131 D7), shown so an operator knows
   *  the ceiling before they hit it rather than after. */
  maxItems?: number
}

/** Provenance for the whole document or a collection (R7) — the "confirmed by / on"
 *  affordance, and the field names it is carried in. */
export interface ProvenanceAffordance {
  path: string
  fields: string[]
}

export interface FormModel {
  fields: FieldDescriptor[]
  provenance: ProvenanceAffordance[]
  /** True when the schema declares `x-valid-time` — the form is a TIMELINE, and the primary
   *  verb becomes "schedule" (D2 / ADR-125 D13). Surfaced here so the shell can switch
   *  vocabulary rather than each field guessing. */
  effectiveDated: boolean
  /** The collection's key property, when it declares one — what a row is identified by. */
  keyProperty?: string
  /** True when the model saw no `x-` keys at all on a schema that declares `properties`.
   *  Almost always means a COMPILED schema was passed by mistake; see the header. */
  looksCompiled: boolean
}

function kindOf(node: Record<string, unknown>): FieldKind {
  if (Array.isArray(node.enum)) return 'enum'
  const type = node.type
  if (type === 'string' || type === 'number' || type === 'integer' || type === 'boolean') {
    return type
  }
  if (type === 'array') return 'array'
  if (type === 'object') return 'object'
  if (Array.isArray(type)) {
    // A type union is how the corpus spells nullable (`["string","null"]`). Render as the
    // non-null member; the null is a value, not a control.
    const primary = type.find((t) => t !== 'null')
    if (typeof primary === 'string') {
      return kindOf({ ...node, type: primary })
    }
  }
  // Anything else — a `oneOf` a human wrote, a $ref — is UNSUPPORTED rather than guessed.
  // D2 puts the escape hatch behind the Raw toggle; rendering a control that cannot express
  // the constraint would produce data the validator then rejects.
  return 'unsupported'
}

/** R12 — the unit goes IN the label, because a bare `0.6` and a bare `20` are the confusion. */
export function labelFor(name: string, node: Record<string, unknown>): string {
  const human = name.replace(/[_-]/g, ' ').replace(/^./, (c) => c.toUpperCase())
  const unit = typeof node['x-unit'] === 'string' ? node['x-unit'] : undefined
  return unit ? `${human} (${unit})` : human
}

function describe(
  name: string,
  node: Record<string, unknown>,
  path: string,
  required: boolean,
): FieldDescriptor {
  const kind = kindOf(node)
  const field: FieldDescriptor = {
    path,
    name,
    label: labelFor(name, node),
    kind,
    required,
  }
  if (typeof node.description === 'string') field.description = node.description
  if (Array.isArray(node.enum)) field.options = node.enum
  if (typeof node['x-unit'] === 'string') field.unit = node['x-unit']
  if (node['x-deprecated'] && typeof node['x-deprecated'] === 'object') {
    field.deprecated = node['x-deprecated'] as Deprecation
  }
  if ('x-unknown-sentinel' in node) field.unknownSentinel = node['x-unknown-sentinel']
  if (typeof node['x-resolve'] === 'string') field.resolvedBy = node['x-resolve']
  if (typeof node['x-resolver'] === 'string') field.resolvedBy = node['x-resolver']
  if (typeof node.maxItems === 'number') field.maxItems = node.maxItems

  if (kind === 'object' && node.properties && typeof node.properties === 'object') {
    field.children = childrenOf(node, path)
  }
  if (kind === 'array') {
    const items = node.items
    if (items && typeof items === 'object' && !Array.isArray(items)) {
      const record = items as Record<string, unknown>
      if (record.properties && typeof record.properties === 'object') {
        field.children = childrenOf(record, `${path}[]`)
      }
    }
  }
  return field
}

function childrenOf(node: Record<string, unknown>, path: string): FieldDescriptor[] {
  const props = (node.properties ?? {}) as Record<string, Record<string, unknown>>
  const required = new Set(Array.isArray(node.required) ? node.required as string[] : [])
  return Object.entries(props).map(([name, sub]) =>
    describe(name, sub, path ? `${path}.${name}` : name, required.has(name)))
}

function collectProvenance(
  node: Record<string, unknown>,
  path: string,
  out: ProvenanceAffordance[],
): void {
  const prov = node['x-provenance']
  if (prov && typeof prov === 'object' && Array.isArray((prov as { fields?: unknown }).fields)) {
    out.push({ path, fields: (prov as { fields: string[] }).fields })
  }
  for (const [name, sub] of Object.entries((node.properties ?? {}) as Record<string, unknown>)) {
    if (sub && typeof sub === 'object') {
      collectProvenance(sub as Record<string, unknown>, path ? `${path}.${name}` : name, out)
    }
  }
  const items = node.items
  if (items && typeof items === 'object' && !Array.isArray(items)) {
    collectProvenance(items as Record<string, unknown>, `${path}[]`, out)
  }
}

function hasAnyAnnotation(node: unknown): boolean {
  if (Array.isArray(node)) return node.some(hasAnyAnnotation)
  if (!node || typeof node !== 'object') return false
  const record = node as Record<string, unknown>
  if (Object.keys(record).some((k) => k.startsWith('x-'))) return true
  return Object.values(record).some(hasAnyAnnotation)
}

/**
 * The node carrying `x-key` — the set's keyed collection.
 *
 * This used to be read off the EFFECTIVE-DATED node, so a set with a key but no
 * `x-valid-time` — which is most of them — reported no key at all. The visible
 * symptom was rows labelled "Row 1" instead of by their key, and a frozen
 * identity column showing an index. `x-key` (R1) and `x-valid-time` (R34) are
 * independent annotations and are now read independently.
 */
function findKeyed(node: unknown): Record<string, unknown> | null {
  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = findKeyed(item)
      if (hit) return hit
    }
    return null
  }
  if (!node || typeof node !== 'object') return null
  const record = node as Record<string, unknown>
  if (typeof record['x-key'] === 'string') return record
  for (const value of Object.values(record)) {
    const hit = findKeyed(value)
    if (hit) return hit
  }
  return null
}

function findValidTime(node: unknown): Record<string, unknown> | null {
  if (Array.isArray(node)) {
    for (const item of node) {
      const hit = findValidTime(item)
      if (hit) return hit
    }
    return null
  }
  if (!node || typeof node !== 'object') return null
  const record = node as Record<string, unknown>
  if (record['x-valid-time'] && typeof record['x-valid-time'] === 'object') return record
  for (const value of Object.values(record)) {
    const hit = findValidTime(value)
    if (hit) return hit
  }
  return null
}

/**
 * Build the form model from the RAW schema — never the compiled one. See the header.
 */
export function formModelFromSchema(schema: unknown): FormModel {
  const empty: FormModel = {
    fields: [], provenance: [], effectiveDated: false, looksCompiled: false,
  }
  if (!schema || typeof schema !== 'object') return empty
  const root = schema as Record<string, unknown>

  const provenance: ProvenanceAffordance[] = []
  collectProvenance(root, '', provenance)

  const dated = findValidTime(root)
  const keyed = findKeyed(root)
  const keyProperty = keyed ? (keyed['x-key'] as string) : undefined

  const hasProperties = !!root.properties && typeof root.properties === 'object'

  return {
    fields: hasProperties ? childrenOf(root, '') : [],
    provenance,
    effectiveDated: dated !== null,
    keyProperty,
    // A schema with properties but not one `x-` key anywhere is almost certainly the
    // COMPILED document, passed by mistake. Reported so the form can say so instead of
    // rendering a plausible editor that silently dropped every layer-2 obligation.
    looksCompiled: hasProperties && !hasAnyAnnotation(root),
  }
}

/** Every descriptor, flattened — for a table view, and for asserting coverage in tests. */
export function flattenFields(fields: FieldDescriptor[]): FieldDescriptor[] {
  return fields.flatMap((f) => [f, ...flattenFields(f.children ?? [])])
}

/**
 * Is this value the field's "not yet known" state? (R6)
 *
 * A separate function because the renderer needs it per value, and because the comparison is
 * the thing that must not become `!value` — for a boolean field with sentinel, `false` and
 * "unknown" are different answers and `!value` cannot tell them apart.
 */
export function isUnknown(field: FieldDescriptor, value: unknown): boolean {
  return field.unknownSentinel !== undefined && value === field.unknownSentinel
}
